import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

export default class ShowcaseMediaUploadController extends Controller {
  static targets = ["input", "container"];

  declare readonly inputTarget: HTMLInputElement;
  declare readonly containerTarget: HTMLElement;

  static values = {
    uploadUrl: String,
    registerUrl: String,
  };

  declare uploadUrlValue: string;
  declare registerUrlValue: string;

  connect(): void {
    this.inputTarget.addEventListener("change", () => {
      void this.onFileSelected();
    });
  }

  directUploadWillStoreFileWithXHR(xhr: XMLHttpRequest): void {
    xhr.setRequestHeader("X-CSRF-Token", this.csrfToken());
  }

  private csrfToken(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
  }

  private async onFileSelected(): Promise<void> {
    const file = this.inputTarget.files?.[0];
    if (!file) return;

    this.inputTarget.disabled = true;

    try {
      const signedBlobId = await this.directUpload(file);
      const response = await fetch(this.registerUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({ signed_blob_id: signedBlobId }),
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? "Upload registration failed.");
      }

      const data = (await response.json()) as { id: number };
      this.appendUploadId(data.id, file.name);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload failed.";
      alert(message);
    } finally {
      this.inputTarget.value = "";
      this.inputTarget.disabled = false;
    }
  }

  private directUpload(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, this.uploadUrlValue, this);
      upload.create((error: Error | null, blob: { signed_id?: string } | null) => {
        if (error) {
          reject(error);
          return;
        }
        if (!blob?.signed_id) {
          reject(new Error("Direct upload did not return a signed blob id."));
          return;
        }
        resolve(blob.signed_id);
      });
    });
  }

  private appendUploadId(id: number, filename: string): void {
    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = "media_upload_ids[]";
    hidden.value = String(id);
    this.containerTarget.appendChild(hidden);

    const label = document.createElement("p");
    label.className = "mt-2 text-xs text-muted-foreground";
    label.textContent = `Uploaded: ${filename}`;
    this.containerTarget.appendChild(label);
  }
}
